"use client";

import { useEffect, useSyncExternalStore } from "react";
import { flushSync } from "react-dom";
import posthog from "posthog-js";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { PrefetchLink } from "@/components/PrefetchLink";
import type { ProjectGridItem } from "@/utils/projects";
import type { ClientSite } from "@/resources/clients";
import { getImageDimensions } from "@/resources/image-dimensions";
import { IndexList, type IndexRow } from "./IndexList";
import styles from "./WorkIndex.module.scss";

interface WorkIndexProps {
  projects: ProjectGridItem[];
  clientSites: ClientSite[];
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "client", label: "Client" },
  { key: "lab", label: "Lab" },
  { key: "university", label: "University" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

const VIEWS = [
  { key: "list", label: "List" },
  { key: "grid", label: "Grid" },
] as const;

type ViewKey = (typeof VIEWS)[number]["key"];

/* Same breakpoint as the mosaic's single-column collapse. */
const MOBILE_QUERY = "(max-width: 767px)";

function subscribeToViewport(onChange: () => void) {
  const media = window.matchMedia(MOBILE_QUERY);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

/* location.search read as an external store instead of useSearchParams:
   the hook forces the whole component into client-only rendering on a
   static page (the SSR HTML would be just the Suspense fallback), which
   is exactly the layout-shift-on-hydration this page cannot afford. The
   server snapshot is "" — the paramless default — so the static HTML is
   the real index and a shared ?category/?view URL applies right after
   hydration. */
const SEARCH_SYNC_EVENT = "oi-search-sync";

function subscribeToSearch(onChange: () => void) {
  window.addEventListener("popstate", onChange);
  window.addEventListener(SEARCH_SYNC_EVENT, onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(SEARCH_SYNC_EVENT, onChange);
  };
}

interface Tile {
  href: string;
  name: string;
  label: string;
  category: Exclude<FilterKey, "all">;
  year: string;
  image: string;
  external?: boolean;
}

export function WorkIndex({ projects, clientSites }: WorkIndexProps) {
  const pathname = usePathname();
  const search = useSyncExternalStore(
    subscribeToSearch,
    () => window.location.search,
    () => "",
  );
  const searchParams = new URLSearchParams(search);

  const isMobile = useSyncExternalStore(
    subscribeToViewport,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false,
  );

  const raw = searchParams.get("category");
  const active: FilterKey = FILTERS.some((f) => f.key === raw) ? (raw as FilterKey) : "all";
  /* No explicit choice: list reads better on desktop, the mosaic on mobile
     (where the list's hover flood and type/year columns pull less weight).
     The paramless default is resolved by CSS media query, not state, so
     SSR and the client agree and hydration never swaps the view (CLS 0);
     matchMedia only informs the toolbar's pressed state. */
  const rawView = searchParams.get("view");
  const viewChoice: ViewKey | null = VIEWS.some((v) => v.key === rawView)
    ? (rawView as ViewKey)
    : null;
  const view: ViewKey = viewChoice ?? (isMobile ? "grid" : "list");

  /* The head stamp script (work-filter-init in layout.tsx) pre-paints a
     shared ?category/?view URL at the CSS layer, since the static HTML is
     always the paramless index. Once hydrated, React renders the filtered
     state itself — drop the stamps so pill clicks aren't fought by a stale
     attribute. The swap is invisible: the DOM React settles on matches
     what the stamps were already showing. */
  useEffect(() => {
    document.documentElement.removeAttribute("data-oi-filter");
    document.documentElement.removeAttribute("data-oi-view");
  }, []);

  const sync = (params: URLSearchParams) => {
    const query = params.toString();
    const apply = () => {
      // shallow update: the filter is computed client-side, no RSC round trip
      window.history.replaceState(null, "", query ? `${pathname}?${query}` : pathname);
      window.dispatchEvent(new Event(SEARCH_SYNC_EVENT));
    };
    /* Filter and view swaps crossfade the results as one view-transition
       group (custom.css). The name is only set for the duration of this
       transition, so the ThemeToggle wipe still captures the page as one
       root snapshot. flushSync lets React commit inside the callback. */
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!document.startViewTransition || reduce.matches) {
      apply();
      return;
    }
    const root = document.documentElement;
    root.dataset.vt = "work";
    const vt = document.startViewTransition(() => flushSync(apply));
    vt.finished.finally(() => delete root.dataset.vt);
  };

  const select = (category: FilterKey) => {
    const params = new URLSearchParams(searchParams.toString());
    if (category === "all") {
      params.delete("category");
    } else {
      params.set("category", category);
    }
    posthog.capture("work_filter_changed", { filter: category });
    sync(params);
  };

  const selectView = (next: ViewKey) => {
    const params = new URLSearchParams(searchParams.toString());
    /* Always explicit: the paramless default is viewport-dependent, so a
       shared URL only pins the view when the user actually picked one. */
    params.set("view", next);
    posthog.capture("work_view_toggled", { view: next });
    sync(params);
  };

  /* One flat inventory: live client sites lead (each linking to its case
     page), then every remaining project newest first (getAllProjects is
     already date-sorted). The client case studies ARE the site tiles, so
     their slugs are excluded from the project half. */
  const caseSlugs = new Set(clientSites.map((site) => site.caseSlug));
  const ownProjects = projects.filter((project) => !caseSlugs.has(project.slug));
  const tiles: Tile[] = [
    ...clientSites.map((site) => ({
      href: `/work/${site.caseSlug}`,
      name: site.name,
      label: site.domain,
      category: "client" as const,
      year: site.year,
      image: site.live[0],
    })),
    ...ownProjects
      .map((project) => ({
        href: project.href,
        name: project.title,
        label: project.category === "client" ? "Case study" : project.categoryLabel,
        category: project.category,
        year: project.year,
        image: project.thumbnail ?? "",
      }))
      .filter((tile) => tile.image),
  ];

  const visible = active === "all" ? tiles : tiles.filter((tile) => tile.category === active);

  const listRows: IndexRow[] = [
    ...(active === "all" || active === "client"
      ? [
          ...clientSites.map((site) => ({
            href: `/work/${site.caseSlug}`,
            name: site.name,
            type: site.sector,
            year: site.year,
            image: site.live[0],
            cat: "client",
          })),
          ...ownProjects
            .filter((project) => project.category === "client")
            .map((project) => ({
              href: project.href,
              name: project.title,
              type: "Case study",
              year: project.year,
              image: project.thumbnail ?? undefined,
              cat: "client",
            })),
        ]
      : []),
    ...ownProjects
      .filter(
        (project) =>
          project.category !== "client" && (active === "all" || project.category === active),
      )
      .map((project) => ({
        href: project.href,
        name: project.title,
        type: project.categoryLabel,
        year: project.year,
        image: project.thumbnail ?? undefined,
        cat: project.category,
      })),
  ];

  return (
    <div className={styles.index} data-view={viewChoice ?? "auto"}>
      <div className="oi-section-head">
        <span className="oi-xs oi-faint">Index / {String(visible.length).padStart(2, "0")}</span>
        <h1 className="oi-h1">Selected works</h1>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters} role="group" aria-label="Filter projects">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`oi-pill${active === filter.key ? " is-active" : ""}`}
              aria-pressed={active === filter.key}
              onClick={() => select(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className={styles.filters} role="group" aria-label="View">
          {VIEWS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`oi-pill${view === option.key ? " is-active" : ""}`}
              aria-pressed={view === option.key}
              onClick={() => selectView(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Always in the DOM so a stamped ?category=university hard load can
          show it before hydration; visibility is CSS (noteShown / stamp). */}
      <p
        className={`oi-xs oi-muted ${styles.note}${
          active === "university" ? ` ${styles.noteShown}` : ""
        }`}
      >
        A partial archive. I put this portfolio off for years and lost most of the originals along
        the way, the development projects especially. What survived is here.
      </p>

      <div className={styles.listWrap}>
        <IndexList rows={listRows} ariaLabel="Selected works" />
      </div>
      <div className={styles.mosaic}>
        {visible.map((tile, i) => {
          const dimensions = getImageDimensions(tile.image);
          /* Match the repeating 4-slot cycle widths: 7 / 4 / 5 / 5 cols. */
          const sizes = `(max-width: 767px) 100vw, ${[58, 33, 42, 42][i % 4]}vw`;
          const inner = (
            <>
              <span className={styles.cellMedia}>
                <Image
                  src={tile.image}
                  alt={tile.name}
                  width={dimensions.width}
                  height={dimensions.height}
                  sizes={sizes}
                  priority={i < 2}
                  fetchPriority={i < 2 ? "high" : undefined}
                />
              </span>
              <span className={styles.caption}>
                <span>
                  {tile.name}
                  <span className="oi-muted">
                    {" "}
                    · {tile.label}
                    {tile.external ? " ↗︎" : ""}
                  </span>
                </span>
                <span className={`oi-xs oi-faint ${styles.captionYear}`}>{tile.year}</span>
              </span>
            </>
          );
          return tile.external ? (
            <a
              key={`${tile.href}-${tile.name}`}
              className={styles.cell}
              href={tile.href}
              target="_blank"
              rel="noreferrer"
              data-cat={tile.category}
            >
              {inner}
            </a>
          ) : (
            <PrefetchLink
              key={`${tile.href}-${tile.name}`}
              className={styles.cell}
              href={tile.href}
              data-cat={tile.category}
            >
              {inner}
            </PrefetchLink>
          );
        })}
      </div>
    </div>
  );
}
